import {
  GridColumn as Column,
  GridExpandChangeEvent,
  GridRowClickEvent
} from '@progress/kendo-react-grid';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  ImportProductDto,
  ImportProductParameterDto
} from 'src/store/imports/import';

type Props = {
  importProducts: ImportProductDto[] | null;
};

export const EurofinsList: React.FC<Props> = props => {
  const { t } = useTranslation();

  const [importProducts, setImportProducts] = useState<ImportProductDto[]>([]);

  useEffect(() => {
    setImportProducts(props.importProducts ?? []);
  }, [props.importProducts]);

  const onImportProductClick = async (
    event: GridRowClickEvent | GridExpandChangeEvent
  ) => {
    setImportProducts(
      importProducts.map((product: ImportProductDto & Expandable) =>
        product.id === event.dataItem.id
          ? {
              ...product,
              expanded: !product.expanded
            }
          : product
      )
    );
  };

  return (
    importProducts && (
      <DataGrid
        items={importProducts}
        filterable={false}
        detail={props => (
          <ImportProductParameters parameters={props.dataItem.parameters} />
        )}
        expandField="expanded"
        onRowClick={onImportProductClick}
        onExpandChange={onImportProductClick}
      >
        <Column
          field="lookupValue"
          width={200}
          title={t('import.lookupValue')}
          footerCell={() => (
            <GridCountCell
              count={importProducts?.length}
              label={t('product.plural')}
            />
          )}
        />
        <Column
          width="120px"
          field="exists"
          title={t('props.exists')}
          cell={props => <BooleanCell {...props} />}
        />
        <Column field="runNo" width={120} title={t('product.runNo')} />
        <Column
          field="runSystemName"
          width={300}
          title={t('runSystem.singular')}
        />
        <Column field="lotNo" width={120} title={t('product.lotNo')} />
        <Column field="gradeName" width={300} title={t('grade.singular')} />
        <Column
          field="productStatus"
          width={200}
          title={t('productStatus.singular')}
        />
        <Column field="description" title={t('props.description')} />
      </DataGrid>
    )
  );
};

const ImportProductParameters: React.FC<{
  parameters: ImportProductParameterDto[];
}> = ({ parameters }) => {
  const { t } = useTranslation();

  return (
    <DataGrid items={parameters} style={{ marginTop: 20 }} filterable={false}>
      <Column
        width="120px"
        field="exists"
        title={t('props.exists')}
        cell={props => <BooleanCell {...props} />}
        footerCell={() => (
          <GridCountCell
            count={parameters?.length}
            label={t('parameter.plural')}
          />
        )}
      />
      <Column
        field="parameterName"
        width={300}
        title={t('parameter.singular')}
      />
      <Column field="methodName" width={300} title={t('method.singular')} />
      <Column
        field="measuredValue"
        title={t('parameter.measuredValue')}
        width={100}
        format="{0:0.000}"
      />
      <Column
        field="importValue"
        title={t('import.importValue')}
        format="{0:0.000}"
        cell={props => (
          <td>
            {props.dataItem.lessThan && <span className="mr-8">&lt;</span>}
            <span className={props.dataItem.lessThan ? '' : 'ml-16'}>
              {props.dataItem.importValue.toFixed(3)}
            </span>
          </td>
        )}
      />
    </DataGrid>
  );
};
